import { useId, useState, type ReactNode } from "react";
import { useSupportLineNumber } from "../hooks/useSupportLineNumber";
import { cn } from "../lib/cn";
import { formatUsPhone } from "../lib/format";
import type { ContactSource } from "../types/api";
import { Modal } from "./Modal";
import { RequestCallForm, type RequestCallCopy, type RequestCallPrefill } from "./RequestCallForm";
import {
  ContactMessageForm,
  type ContactMessageCopy,
  type ContactMessagePrefill,
  type ContactTopic,
} from "./ContactMessageForm";

/** Modal-level strings: the title, the two tab labels and the lead-in before
 *  the support-line number. Per-tab form copy lives on `callCopy` /
 *  `messageCopy`. */
export interface ContactUsModalCopy {
  title: string;
  callTabLabel: string;
  messageTabLabel: string;
  /** Precedes the support-line `tel:` link. */
  callUsLabel: string;
}

const DEFAULT_CONTACT_US_COPY: ContactUsModalCopy = {
  title: "Contact us",
  callTabLabel: "Request Call",
  messageTabLabel: "Message Us",
  callUsLabel: "Prefer to call?",
};

export interface ContactUsModalProps {
  open: boolean;
  onClose: () => void;
  /** Which SPA the submission came from; both tabs send it. */
  source: ContactSource;
  /** `import.meta.env.VITE_API_BASE_URL ?? ""` from the calling SPA. */
  baseUrl: string;
  /** "Message Us" topic options (per-SPA; values stay canonical English). */
  topics: ContactTopic[];
  /** Identity prefill for the "Request Call" tab; everything stays editable. */
  callPrefill?: RequestCallPrefill | undefined;
  /** Email/phone prefill (+ read-only locks) for the "Message Us" tab. */
  messagePrefill?: ContactMessagePrefill | undefined;
  /** Optional bearer (member or partner session), used by the message POST
   *  only. `/contact` is public, so omitting it is harmless. */
  token?: string | undefined;
  /** Localized overrides for the title + tab labels. */
  copy?: Partial<ContactUsModalCopy> | undefined;
  /** Localized overrides for the "Request Call" form. */
  callCopy?: Partial<RequestCallCopy> | undefined;
  /** Localized overrides for the "Message Us" form. */
  messageCopy?: Partial<ContactMessageCopy> | undefined;
  /** BCP-47 locale for the call-tab success date. Defaults to `"en"`. */
  dateLocale?: string | undefined;
  /** Extra content above the "Request Call" form, such as a link to an SPA's own booking page. */
  callTabNote?: ReactNode | undefined;
}

/**
 * Two-tab "Contact us" support modal for the customer app and the member and partner portals.
 *
 * "Request Call", the default tab, posts a `CallRequestCreate`; "Message Us"
 * posts a `ContactCreate`. Above the tabs, a "Prefer to call?" line links the
 * configured support line as `tel:`. Without a configured number, or when the
 * lookup fails, the line is omitted.
 *
 * The body (tab state and panels) mounts fresh each time the modal opens, so
 * the active tab resets to "Request Call" and the fields re-initialize from the
 * prefills without a reset effect. Only the active panel is mounted, so
 * switching tabs discards the other form's in-progress input.
 */
export function ContactUsModal(props: ContactUsModalProps): ReactNode {
  const { open, onClose } = props;
  const copy: ContactUsModalCopy = { ...DEFAULT_CONTACT_US_COPY, ...props.copy };

  return (
    <Modal open={open} title={copy.title} onClose={onClose}>
      <ContactUsBody {...props} copy={copy} />
    </Modal>
  );
}

type ContactTab = "call" | "message";

interface ContactUsBodyProps extends Omit<ContactUsModalProps, "copy"> {
  copy: ContactUsModalCopy;
}

function ContactUsBody({
  onClose,
  source,
  baseUrl,
  topics,
  callPrefill,
  messagePrefill,
  token,
  copy,
  callCopy,
  messageCopy,
  dateLocale,
  callTabNote,
}: ContactUsBodyProps): ReactNode {
  const [tab, setTab] = useState<ContactTab>("call");
  const supportLine = useSupportLineNumber(baseUrl);
  const idBase = useId();
  const tabId = (t: ContactTab): string => `${idBase}-tab-${t}`;
  const panelId = (t: ContactTab): string => `${idBase}-panel-${t}`;

  const tabButton = (t: ContactTab, label: string): ReactNode => (
    <button
      type="button"
      role="tab"
      id={tabId(t)}
      aria-selected={tab === t}
      aria-controls={panelId(t)}
      onClick={() => setTab(t)}
      className={cn(
        "-mb-px cursor-pointer border-b-2 px-3 py-2 text-sm font-medium",
        tab === t
          ? "border-ember text-ember"
          : "border-transparent text-slate-500 hover:text-slate-700",
      )}
    >
      {label}
    </button>
  );

  return (
    <div>
      {supportLine && (
        <p className="text-slate-teal mb-4 text-base">
          {copy.callUsLabel}{" "}
          <a
            href={`tel:${supportLine}`}
            className="text-primary-text hover:text-ember-dark whitespace-nowrap font-semibold"
          >
            {formatUsPhone(supportLine)}
          </a>
        </p>
      )}
      <div
        role="tablist"
        aria-label={copy.title}
        className="mb-4 flex gap-2 border-b border-slate-200"
      >
        {tabButton("call", copy.callTabLabel)}
        {tabButton("message", copy.messageTabLabel)}
      </div>
      <div role="tabpanel" id={panelId(tab)} aria-labelledby={tabId(tab)}>
        {tab === "call" ? (
          <>
            {callTabNote}
            <RequestCallForm
              onClose={onClose}
              source={source}
              baseUrl={baseUrl}
              prefill={callPrefill}
              copy={callCopy}
              dateLocale={dateLocale}
            />
          </>
        ) : (
          <ContactMessageForm
            onClose={onClose}
            source={source}
            topics={topics}
            baseUrl={baseUrl}
            prefill={messagePrefill}
            token={token}
            copy={messageCopy}
          />
        )}
      </div>
    </div>
  );
}
